const roundOrderCurrency = (value) => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? Number(numeric.toFixed(2)) : 0;
};

const parseOrderMetadata = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  return value && typeof value === "object" ? value : {};
};

const pickFiniteNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return null;
};

const getOrderItemLineAmount = (item = {}) => {
  const directAmount = pickFiniteNumber(
    item.line_total,
    item.total,
    item.final_total,
    item.amount,
  );

  if (directAmount !== null) {
    return roundOrderCurrency(directAmount);
  }

  const quantity = Number(item.quantity || item.qty || 0);
  const unitPrice = Number(item.unit_price ?? item.unitPrice ?? item.price ?? 0);
  const discount = Number(
    item.discount ??
      item.discountAmount ??
      item.offer_discount_amount ??
      0,
  );

  if (!Number.isFinite(quantity) || quantity <= 0) {
    return 0;
  }

  return roundOrderCurrency((unitPrice - discount) * quantity);
};

export const getOrderPricingSummary = (order = {}) => {
  const metadata = parseOrderMetadata(order.metadata);
  const items = Array.isArray(order.items) ? order.items : [];
  const derivedSubtotal = roundOrderCurrency(
    items.reduce((sum, item) => sum + getOrderItemLineAmount(item), 0),
  );
  const deliveryCharge = roundOrderCurrency(
    pickFiniteNumber(
      metadata.shipping_charge,
      metadata.delivery_charge,
      order.shipping_charge,
      order.delivery_charge,
      0,
    ) ?? 0,
  );
  const configuredVatRaw = pickFiniteNumber(
    metadata.vat_amount,
    order.vat_amount,
  );
  const configuredVatAmount =
    configuredVatRaw === null ? null : roundOrderCurrency(configuredVatRaw);
  const orderTotal = roundOrderCurrency(
    pickFiniteNumber(order.total_amount, metadata.total_amount, 0) ?? 0,
  );
  const fallbackSubtotal = roundOrderCurrency(
    orderTotal - deliveryCharge - (configuredVatAmount ?? 0),
  );
  const subtotal = roundOrderCurrency(
    pickFiniteNumber(
      metadata.subtotal_amount,
      derivedSubtotal > 0 ? derivedSubtotal : null,
      fallbackSubtotal,
      0,
    ) ?? 0,
  );
  const derivedVat = roundOrderCurrency(orderTotal - subtotal - deliveryCharge);
  const vatAmount = roundOrderCurrency(
    configuredVatAmount !== null ? configuredVatAmount : Math.max(derivedVat, 0),
  );
  const discountAmount = roundOrderCurrency(
    pickFiniteNumber(metadata.discount, order.discount, 0) ?? 0,
  );

  return {
    metadata,
    subtotal,
    deliveryCharge,
    vatAmount,
    discountAmount,
    total: orderTotal || roundOrderCurrency(subtotal + deliveryCharge + vatAmount),
  };
};
